import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Clock, User, Share2, Linkedin, Twitter, Facebook, AlertTriangle, Layers, DollarSign, GraduationCap, Workflow } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useBlogContactPopup } from "@/hooks/useBlogContactPopup";
import DemoScheduler from "@/components/DemoScheduler";
import SEO from "@/components/SEO";

const BlogPostPlataformaUnificada = () => {
    const { isOpen, setIsOpen } = useBlogContactPopup(3000);

    return (
        <div className="min-h-screen bg-background">
            <SEO
                title="5 Sinais de Que Sua Empresa Precisa de Uma Plataforma Unificada"
                description="Identifique se sua empresa sofre com fragmentação tecnológica. Descubra os 5 sinais claros de que é hora de consolidar ferramentas em uma plataforma unificada e reduzir custos operacionais."
                canonical="https://aroan.com.br/blog/sinais-plataforma-unificada"
                ogImage="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&auto=format&fit=crop"
                author="João Santos"
                publishedTime="2024-11-05T00:00:00-03:00"
                keywords="plataforma unificada, fragmentação tecnológica, silos de dados, redução de custos, integração de sistemas, eficiência operacional"
            />
            <Header />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-gradient-hero overflow-hidden">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                            Gestão de Serviços
                        </Badge>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight animate-fade-in">
                            5 Sinais de Que Sua Empresa Precisa de <span className="text-primary">Uma Plataforma Unificada</span>
                        </h1>

                        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in-delay-1">
                            <div className="flex items-center gap-2">
                                <User className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">João Santos</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">05 Nov, 2024</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">6 min de leitura</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Image */}
            <section className="pb-12">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl animate-scale-in">
                        <img
                            src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&auto=format&fit=crop"
                            alt="Plataforma Unificada"
                            className="w-full h-[400px] sm:h-[500px] object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto">

                        {/* Introduction */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <p className="lead text-xl sm:text-2xl text-muted-foreground leading-relaxed font-medium">
                                Você já parou para contar quantas ferramentas sua equipe usa diariamente? E-mail, planilhas, CRM, ERP, sistema de chamados, chat interno... A lista parece infinita.
                            </p>
                            <p>
                                Embora cada ferramenta tenha sua utilidade, o excesso delas cria um fenômeno conhecido como <strong>fragmentação tecnológica</strong>. Em vez de ajudar, essa colcha de retalhos de softwares acaba criando silos de dados, ineficiência e frustração.
                            </p>
                            <p>
                                Se você suspeita que sua operação está sofrendo com isso, confira os 5 sinais claros de que chegou a hora de consolidar tudo em uma plataforma unificada.
                            </p>
                        </div>

                        <hr className="my-12 border-border" />

                        {/* Signs */}
                        <div className="space-y-12 mb-12">

                            {/* Sign 1 */}
                            <div className="bg-card rounded-2xl p-8 border border-border hover:shadow-lg transition-all">
                                <div className="flex items-start gap-4">
                                    <div className="bg-primary/10 p-3 rounded-lg">
                                        <AlertTriangle className="w-8 h-8 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold mb-3">1. A "Fadiga do Alt-Tab"</h3>
                                        <p className="text-muted-foreground leading-relaxed">
                                            Seus colaboradores passam mais tempo alternando entre janelas do que realmente trabalhando? Estudos mostram que o custo cognitivo de trocar de contexto constantemente pode reduzir a produtividade em até 40%. Se para resolver um único problema o agente precisa abrir 4 sistemas diferentes, você tem um problema de fragmentação.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Sign 2 */}
                            <div className="bg-card rounded-2xl p-8 border border-border hover:shadow-lg transition-all">
                                <div className="flex items-start gap-4">
                                    <div className="bg-primary/10 p-3 rounded-lg">
                                        <Layers className="w-8 h-8 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold mb-3">2. Silos de Dados e Visibilidade Limitada</h3>
                                        <p className="text-muted-foreground leading-relaxed">
                                            Quando a diretoria pede um relatório consolidado, sua equipe entra em pânico? Se você precisa exportar dados de três lugares diferentes e passar horas no Excel para ter uma visão geral da operação, seus dados estão em silos. Uma plataforma unificada oferece dashboards em tempo real com a verdade única sobre o negócio.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Sign 3 */}
                            <div className="bg-card rounded-2xl p-8 border border-border hover:shadow-lg transition-all">
                                <div className="flex items-start gap-4">
                                    <div className="bg-primary/10 p-3 rounded-lg">
                                        <DollarSign className="w-8 h-8 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold mb-3">3. Custos de Licenciamento Redundantes</h3>
                                        <p className="text-muted-foreground leading-relaxed">
                                            Você está pagando por funcionalidades duplicadas? Muitas vezes, o CRM tem um módulo de chat, o sistema de suporte também, e você ainda paga uma ferramenta dedicada de comunicação. Unificar plataformas não é apenas sobre eficiência operacional, é sobre <strong>redução direta de custos</strong> de TI.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Sign 4 */}
                            <div className="bg-card rounded-2xl p-8 border border-border hover:shadow-lg transition-all">
                                <div className="flex items-start gap-4">
                                    <div className="bg-primary/10 p-3 rounded-lg">
                                        <GraduationCap className="w-8 h-8 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold mb-3">4. Onboarding Lento e Complexo</h3>
                                        <p className="text-muted-foreground leading-relaxed">
                                            Quanto tempo leva para um novo funcionário se tornar produtivo? Se ele precisa aprender a navegar em 5 interfaces diferentes, decorar 5 senhas e entender 5 fluxos de trabalho distintos, o onboarding será lento e doloroso. Uma interface unificada reduz drasticamente a curva de aprendizado.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Sign 5 */}
                            <div className="bg-card rounded-2xl p-8 border border-border hover:shadow-lg transition-all">
                                <div className="flex items-start gap-4">
                                    <div className="bg-primary/10 p-3 rounded-lg">
                                        <Workflow className="w-8 h-8 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold mb-3">5. Falta de Automação End-to-End</h3>
                                        <p className="text-muted-foreground leading-relaxed">
                                            Suas automações param no meio do caminho porque os sistemas não conversam? A verdadeira automação inteligente precisa fluir de ponta a ponta. Se o gatilho em um sistema não gera ação no outro sem intervenção humana, você não tem automação, tem apenas tarefas digitais manuais.
                                        </p>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Conclusion */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <h2 className="text-3xl font-bold mb-6 text-foreground">A Solução: Unificação com Inteligência</h2>
                            <p>
                                Reconhecer esses sinais é o primeiro passo. O próximo é buscar uma solução que não apenas integre, mas unifique sua operação.
                            </p>
                            <p>
                                A plataforma AROAN foi desenhada exatamente para resolver esses desafios, centralizando gestão de serviços, automação e inteligência artificial em um único hub. O resultado? Menos custo, mais visibilidade e uma equipe muito mais feliz e produtiva.
                            </p>
                        </div>

                        {/* Share */}
                        <div className="flex items-center justify-between border-y border-border py-6 mb-12">
                            <span className="font-semibold">Compartilhe este artigo:</span>
                            <div className="flex gap-4">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-600"
                                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Linkedin className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-400"
                                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent("5 Sinais de Que Sua Empresa Precisa de Uma Plataforma Unificada")}`, '_blank')}
                                >
                                    <Twitter className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-700"
                                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Facebook className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={async () => {
                                        if (navigator.share) {
                                            try {
                                                await navigator.share({
                                                    title: '5 Sinais de Que Sua Empresa Precisa de Uma Plataforma Unificada',
                                                    text: 'Identifique se sua organização está perdendo eficiência com ferramentas fragmentadas.',
                                                    url: window.location.href,
                                                });
                                            } catch (err) {
                                                console.error('Error sharing:', err);
                                            }
                                        } else {
                                            navigator.clipboard.writeText(window.location.href);
                                            alert("Link copiado para a área de transferência!");
                                        }
                                    }}
                                >
                                    <Share2 className="w-5 h-5" />
                                </Button>
                            </div>
                        </div>

                        {/* CTA */}
                        <div className="bg-foreground text-background rounded-2xl p-8 sm:p-12 text-center">
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                                Identificou algum desses sinais?
                            </h2>
                            <p className="text-background/80 mb-8 max-w-2xl mx-auto">
                                Calcule agora mesmo quanto a fragmentação está custando para sua empresa com nossa calculadora de ROI.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button size="lg" variant="secondary" className="group" onClick={() => window.open('/#roi', '_self')}>
                                    Calcular meu ROI
                                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                                </Button>
                                <Button size="lg" variant="outline" className="bg-transparent border-background/20 text-background hover:bg-background/10" onClick={() => window.open('/#demo', '_self')}>
                                    Agendar Demonstração
                                </Button>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <Footer />

            <DemoScheduler open={isOpen} onOpenChange={setIsOpen} />
        </div>
    );
};

export default BlogPostPlataformaUnificada;
